import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CheckCircleOutlined, SearchOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';

import { useResponsive } from '../../hooks/useResponsive';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { IAssessmentsState } from '../../store/reducers/assessments/types';
import { IClassesState } from '../../store/reducers/classes/types';
import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { PageContainer } from '../../components/layouts/PageLayouts/PageContainer/PageContainer';
import { ContentCard } from '../../components/layouts/PageLayouts/ContentCard/ContentCard';
import { BusinessButton } from '../../components/common/buttons/BusinessButton/BusinessButton';
import { notificationController } from '../../controllers/notificationController';

// Styled Components
const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .title {
    font-size: 24px;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &::before {
      content: '📤';
      font-size: 28px;
    }
  }
  
  .subtitle {
    font-size: 14px;
    color: rgba(44, 62, 80, 0.7);
    margin: 4px 0 0 36px;
  }
  
  @media (max-width: 768px) {
    .title {
      font-size: 20px;
      
      &::before {
        font-size: 24px;
      }
    }
    
    .subtitle {
      margin-left: 32px;
      font-size: 13px;
    }
  }
`;

const SearchInput = styled.div`
  position: relative;
  min-width: 280px;
  
  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
  
  input {
    width: 100%;
    padding: 10px 12px 10px 40px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    font-size: 14px;
    background: white;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #1677ff;
      box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
    }
    
    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }
  }
  
  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

// Assessment Cards Styles
const AssessmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 16px;
  }
`;

const AssessmentCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: rgba(34, 197, 94, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #22c55e 0%, #16a34a 50%, #15803d 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  @media (max-width: 1024px) {
    &:hover {
      transform: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
    
    &::before {
      opacity: 1;
    }
    
    &:active {
      transform: scale(0.98);
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
`;

const CardTitle = styled.div`
  flex: 1;
  
  .icon {
    font-size: 18px;
    margin-bottom: 8px;
    display: block;
  }
  
  .name {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 4px 0;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .date {
    font-size: 12px;
    color: #6B7280;
    font-weight: 500;
  }
`;

const StatusBadge = styled.div<{ status?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: ${props => {
    if (props.status === 'published') return 'rgba(22, 119, 255, 0.1)';
    if (props.status === 'active') return 'rgba(34, 197, 94, 0.1)';
    return 'rgba(156, 163, 175, 0.1)';
  }};
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => {
    if (props.status === 'published') return '#1677ff';
    if (props.status === 'active') return '#16A34A';
    return '#6B7280';
  }};
  white-space: nowrap;
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => {
      if (props.status === 'published') return '#1677ff';
      if (props.status === 'active') return '#22C55E';
      return '#9CA3AF';
    }};
    animation: pulse 1.5s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.3); }
  }
`;

const CardInfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
`;

const CardInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  
  .row-icon {
    font-size: 14px;
    width: 20px;
    text-align: center;
    flex-shrink: 0;
  }
  
  .row-label {
    color: #6B7280;
    min-width: 60px;
    flex-shrink: 0;
  }
  
  .row-value {
    color: #1a1a1a;
    font-weight: 500;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SelectButton = styled(BusinessButton)`
  width: 100%;
`;

const ErrorAlert = styled.div`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: rgba(255, 77, 79, 0.08);
  border: 1px solid rgba(255, 77, 79, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  
  .error-icon {
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  .error-content {
    flex: 1;
    
    .error-title {
      font-size: 13px;
      font-weight: 600;
      color: #ff4d4f;
      margin-bottom: 4px;
    }
    
    .error-message {
      font-size: 12px;
      color: #8c8c8c;
      line-height: 1.5;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(0, 0, 0, 0.45);
  
  .icon {
    font-size: 64px;
    margin-bottom: 16px;
    display: block;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.65);
  }
  
  .description {
    font-size: 14px;
    margin-bottom: 24px;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const LoadingState = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const SkeletonCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  
  .skeleton-line {
    height: 12px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 6px;
    margin-bottom: 12px;
    
    &.title {
      height: 20px;
      width: 70%;
    }
    
    &.subtitle {
      height: 16px;
      width: 90%;
    }
    
    &.meta {
      height: 12px;
      width: 50%;
    }
  }
  
  @keyframes loading {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const AddToGradingPage: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Redux
  const { assessments, loading } = useTypedSelector<IAssessmentsState>(state => state.assessments);
  const { classes } = useTypedSelector<IClassesState>(state => state.classes);
  const { getAssessmentsRequest, getClassesRequest } = useActions();
  
  // Local state
  const [searchQuery, setSearchQuery] = useState<string>('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectAssessment = (assessment: any) => {
    const assessmentName = encodeURIComponent(assessment.name || assessment.examName || 'Assessment');
    navigate(`/upload-answer-sheets?assessmentId=${assessment.id}&name=${assessmentName}`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getClassName = (classId: number) => {
    const classesArray = classes as any[];
    const cls = classesArray?.find((c) => c.id === classId);
    return cls?.name || `Class ${classId}`;
  };

  // Filter published and active assessments
  const activeAssessments = (assessments || [])
    .filter((assessment: any) => {
      const status = assessment.status?.toLowerCase();
      console.log('[Add to Grading] Assessment:', assessment.name || assessment.examName, 'Status:', assessment.status, 'Error:', assessment.error || 'none');
      return status === 'published' || status === 'active';
    })
    .filter((assessment: any) =>
      (assessment.examName || assessment.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Log filtered results
  console.log('[Add to Grading] Total assessments:', assessments?.length || 0);
  console.log('[Add to Grading] Published/Active assessments:', activeAssessments.length);

  useEffect(() => {
    // Load assessments
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null') {
      if (!searchParams.get('userId')) {
        setSearchParams({ userId: storedUserId });
      }
      
      getAssessmentsRequest();
      getClassesRequest();
    }
  }, []);

  return (
    <>
      <PageTitle 
        title="Add to Grading" 
        description="Select an assessment to upload answer sheets" 
        url={window.location.href} 
        noIndex={true}
      />
      
      <PageContainer>
        {/* Header Section */}
        <HeaderSection>
          <TitleSection>
            <div>
              <h1 className="title">Add to Grading</h1>
              <p className="subtitle">Select an assessment to upload scanned answer sheets</p>
            </div>
          </TitleSection>
          
          <SearchInput>
            <SearchOutlined className="search-icon" />
            <input
              type="text"
              placeholder="Search assessments..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </SearchInput>
        </HeaderSection>

        {/* Content Section */}
        <ContentCard>
          {loading ? (
            <LoadingState>
              {[1, 2, 3, 4].map((item) => (
                <SkeletonCard key={item}>
                  <div className="skeleton-line title"></div>
                  <div className="skeleton-line subtitle"></div>
                  <div className="skeleton-line meta"></div>
                </SkeletonCard>
              ))}
            </LoadingState>
          ) : activeAssessments.length > 0 ? (
            <AssessmentsGrid>
              {activeAssessments.map((assessment: any) => {
                const hasError = assessment.error && assessment.error.trim() !== '';
                
                return (
                <AssessmentCard key={assessment.id} onClick={() => handleSelectAssessment(assessment)}>
                  <CardHeader>
                    <CardTitle>
                      <span className="icon">📝</span>
                      <h3 className="name">{assessment.examName || assessment.name || 'Untitled Assessment'}</h3>
                      <p className="date">
                        📅 {formatDate(assessment.scheduledDate)}
                      </p>
                    </CardTitle>
                    <StatusBadge status={assessment.status?.toLowerCase()}>
                      {assessment.status?.toLowerCase() === 'published' ? 'Published' : 
                       assessment.status?.toLowerCase() === 'active' ? 'Active' : 
                       assessment.status || 'Unknown'}
                    </StatusBadge>
                  </CardHeader>
                  
                  <CardInfoList>
                    {/* Type */}
                    <CardInfoRow>
                      <span className="row-icon">📝</span>
                      <span className="row-label">Type</span>
                      <span className="row-value" style={{ textTransform: 'capitalize' }}>
                        {assessment.type || 'quiz'}
                      </span>
                    </CardInfoRow>
                    
                    {/* Classes */}
                    <CardInfoRow>
                      <span className="row-icon">🏫</span>
                      <span className="row-label">Classes</span>
                      <span className="row-value">
                        {assessment.classIds?.length 
                          ? assessment.classIds.map((id: number) => getClassName(id)).join(', ')
                          : assessment.classId 
                            ? getClassName(assessment.classId)
                            : '—'}
                      </span>
                    </CardInfoRow>
                    
                    {/* Duration */}
                    <CardInfoRow>
                      <span className="row-icon">⏱️</span>
                      <span className="row-label">Duration</span>
                      <span className="row-value">
                        {assessment.duration && assessment.duration > 0
                          ? `${assessment.duration} min`
                          : '—'}
                      </span>
                    </CardInfoRow>
                  </CardInfoList>
                  
                  {/* Show error alert if recognition failed */}
                  {hasError && (
                    <ErrorAlert>
                      <div className="error-icon">⚠️</div>
                      <div className="error-content">
                        <div className="error-title">Recognition Failed</div>
                        <div className="error-message">
                          {assessment.error || 'An error occurred during recognition. Please try uploading again.'}
                        </div>
                      </div>
                    </ErrorAlert>
                  )}
                  
                  <SelectButton
                    variant={hasError ? 'warning' : 'success'}
                    businessSize="medium"
                    icon={hasError ? <ReloadOutlined /> : <CheckCircleOutlined />}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleSelectAssessment(assessment);
                    }}
                  >
                    {hasError 
                      ? 'Try Again' 
                      : `Select ${(assessment.type || 'quiz').charAt(0).toUpperCase() + (assessment.type || 'quiz').slice(1)}`
                    }
                  </SelectButton>
                </AssessmentCard>
                );
              })}
            </AssessmentsGrid>
          ) : (
            <EmptyState>
              <span className="icon">📝</span>
              <div className="title">
                {searchQuery ? 'No matching assessments found' : 'No available assessments'}
              </div>
              <div className="description">
                {searchQuery 
                  ? `Try adjusting your search for "${searchQuery}"` 
                  : 'There are no active or published assessments at the moment. Create and publish an assessment to upload answer sheets for grading.'}
              </div>
            </EmptyState>
          )}
        </ContentCard>
      </PageContainer>
    </>
  );
};

export default AddToGradingPage;
